/**
 * What this page lends to the workbench, said out loud.
 *
 * This is the panel a judge should be able to read in ten seconds and come away
 * knowing that they are looking at one half of a two origin demonstration rather
 * than at a page with a nice catalogue on it. So it names the other origin, names
 * all three tools, says how the workbench reaches them, and then reports what the
 * browser actually did with each registration.
 *
 * ### Why the state readout is here rather than in a console
 *
 * The page usually runs inside an iframe on the workbench, and a frame without
 * `allow="tools"` refuses registration outright. That is a fact about the
 * deployment, not a bug, and there is nowhere else for it to surface: the tools are
 * offered to somebody else's origin, so success is invisible from here unless the
 * page says so. Three rows, each either offered or carrying the browser's own reason
 * for refusing, is the whole of it.
 *
 * ### The names are read, not written
 *
 * `yarnToolNames` reads them off the descriptors in `@jig/tools`, which is the same
 * set the registration uses and the same set the workbench asks for. A panel with
 * its own hardcoded list would eventually advertise a tool this service does not
 * register, which is a worse failure than advertising none.
 */

import { ORIGINS } from '@jig/origins';

import { yarnToolNames, type YarnToolOutcomes } from '../register';

/**
 * One line each, in a knitter's terms rather than an agent's.
 *
 * The descriptors carry their own descriptions and those are written for a model
 * under a character budget, so they are the wrong thing to print here. Keyed on the
 * name rather than on position, because the order the three are attempted in is
 * `registerYarnTools`'s business and this panel should not quietly depend on it.
 */
const ANSWERS: ReadonlyArray<{ readonly ending: string; readonly line: string }> = [
  {
    ending: 'find_yarn',
    line: 'Search the stock by words or by weight. Comes back with ids to look up.',
  },
  {
    ending: 'yarn_profile',
    line: 'One yarn in full: the ball band range, our measured swatch, skein length, fibre.',
  },
  {
    ending: 'substitution_candidates',
    line: 'The nearest yarns by measured gauge, with the notes knitters sent in about them.',
  },
];

const answerFor = (name: string): string =>
  ANSWERS.find((entry) => name.endsWith(entry.ending))?.line ??
  'A read only lookup over this catalogue.';

/**
 * What a row says about itself, so the word and the colour cannot disagree.
 *
 * `unsupported` is kept apart from `refused` on purpose, and it is the state most
 * readers will actually see. A browser with no `document.modelContext` has not
 * refused anything and nothing has gone wrong: the surface is a draft API and most
 * builds do not carry it. Painting that in the same alarming colour as a frame that
 * was denied permission would be a lie about whose fault it is, and it would make a
 * page that is working correctly look broken.
 */
export type RowState = 'pending' | 'offered' | 'refused' | 'unsupported';

const stateWord = (state: RowState): string => {
  switch (state) {
    case 'pending':
      return 'offering';
    case 'offered':
      return 'offered';
    case 'refused':
      return 'not offered';
    case 'unsupported':
      return 'no surface here';
  }
};

interface Row {
  readonly name: string;
  readonly state: RowState;
  /** The browser's own words, where it did not take. */
  readonly why: string | null;
}

/**
 * The three rows, whether or not the registrations have settled.
 *
 * A null set of outcomes is the moment between mount and the first resolution, and
 * it draws the same three names in a pending state rather than an empty box that
 * jumps to three rows. Names come from the descriptors either way.
 */
export const federationRows = (outcomes: YarnToolOutcomes | null): ReadonlyArray<Row> => {
  if (outcomes === null) {
    return yarnToolNames(ORIGINS).map((name) => ({ name, state: 'pending', why: null }));
  }

  return outcomes.map((outcome) =>
    outcome.ok
      ? { name: outcome.name, state: 'offered', why: null }
      : {
          name: outcome.name,
          state: outcome.cause === 'Unsupported' ? 'unsupported' : 'refused',
          why: `${outcome.cause}. ${outcome.message}`,
        },
  );
};

/**
 * The sentence under the three rows.
 *
 * Four cases rather than a count, because a count is the least useful thing to say in
 * three of them. A browser with no surface needs telling that the page is not broken;
 * a frame that was denied permission needs telling which permission; and a page where
 * all three took should say who they are exposed to, since that is the fact the whole
 * arrangement rests on.
 */
export const federationFoot = (rows: ReadonlyArray<Row>): string => {
  if (rows.some((row) => row.state === 'pending')) {
    return 'Registering with the browser now.';
  }

  const offered = rows.filter((row) => row.state === 'offered').length;

  if (rows.every((row) => row.state === 'unsupported')) {
    return `This browser does not carry the WebMCP tool surface, so none of the three could be registered. Nothing else on this page depends on it: the catalogue reads the same either way, and a browser that has the surface sees all three offered to ${ORIGINS.jig}.`;
  }

  if (offered === rows.length) {
    return `All ${rows.length} registered on this browser, and every one of them is read only. They are exposed to ${ORIGINS.jig} and to no other origin.`;
  }

  return `${offered} of ${rows.length} registered on this browser. The rest carry the browser's own reason above. Anything that does register is read only and is exposed to ${ORIGINS.jig} and to no other origin.`;
};

export interface FederationPanelProps {
  readonly outcomes: YarnToolOutcomes | null;
}

export const FederationPanel = ({ outcomes }: FederationPanelProps): JSX.Element => {
  const rows = federationRows(outcomes);

  return (
    <section aria-labelledby="federation-title" className="federation" data-region="federation">
      <div className="federation__head">
        <p className="eyebrow">WebMCP, across two origins</p>
        <h2 className="federation__title" id="federation-title">
          This catalogue lends three tools to the Jig pattern workbench
        </h2>
        <p className="federation__lead">
          Everything on this page belongs to a different site from the workbench that
          reads it. The workbench embeds us in an iframe carrying{' '}
          <code className="num">allow=&quot;tools&quot;</code>, asks the browser for{' '}
          <code className="num">getTools({'{ fromOrigins }'})</code> naming{' '}
          <span className="federation__origin num">{ORIGINS.yarns}</span>, and calls what
          it finds with <code className="num">executeTool</code>. We answer, and nothing
          crosses back but text.
        </p>
      </div>

      <ul className="federation__tools">
        {rows.map((row) => (
          <li className="tool" data-state={row.state} key={row.name}>
            <span className="tool__head">
              <span className="tool__name num">{row.name}</span>
              <span className="tool__state">
                <span aria-hidden="true" className="tool__dot" />
                {stateWord(row.state)}
              </span>
            </span>
            <p className="tool__answers">{answerFor(row.name)}</p>
            {/*
              The browser's reason, but only where it refused. A browser with no tool
              surface refuses all three for the same reason, and printing that reason
              three times over says nothing the sentence under the list does not say
              once, while costing five lines of height on every row.
            */}
            {row.state === 'refused' && row.why !== null ? (
              <p className="tool__why">{row.why}</p>
            ) : null}
          </li>
        ))}
      </ul>

      <p className="federation__foot" role="status">
        {federationFoot(rows)}
      </p>
    </section>
  );
};
